"use client";
import { useState, useRef, useEffect } from "react";
import { motion } from "motion/react";
import { useRouter } from "next/navigation";
import { 
  Shield, 
  Lock, 
  Eye, 
  Users, 
  Server, 
  MessageSquare, 
  Info, 
  ChevronRight,
  Check,
  Clock,
  Globe
} from "lucide-react";

export default function PrivacyPage() {
  const [activeSection, setActiveSection] = useState<string | null>("introduction");
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const observerRef = useRef<IntersectionObserver | null>(null);
  const isScrollingRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const router = useRouter();

  // Cleanup function to be used in multiple places
  const cleanupAllEffects = () => {
    // Clean up observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    
    // Clear any pending timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Reset any ongoing scroll state
    isScrollingRef.current = false;
    
    // Remove any inline styles that might have been added to the html/body
    if (typeof document !== 'undefined') {
      document.documentElement.style.removeProperty('scroll-behavior');
      document.body.style.removeProperty('overflow');
    }
  };

  // Listen for route changes to clean up
  useEffect(() => {
    // This will run when the component mounts
    return () => {
      // This will run when the component unmounts (route change)
      cleanupAllEffects();
    };
  }, [router]);

  // Setup intersection observer to detect active section on scroll
  useEffect(() => {
    // Clean up any existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const options = {
      root: null,
      rootMargin: "-100px 0px -40% 0px",
      threshold: 0.2
    };

    observerRef.current = new IntersectionObserver((entries) => {
      // Don't update active section if user is currently clicking to scroll
      if (isScrollingRef.current) return;

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, options);

    // Observe all section elements
    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observerRef.current?.observe(ref);
    });

    // Cleanup on unmount or deps change
    return () => {
      cleanupAllEffects();
    };
  }, []);

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const element = sectionRefs.current[id];
    if (!element) return;

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set a flag to prevent intersection observer from changing active section during programmatic scrolling
    isScrollingRef.current = true;

    // Use a more contained approach for scrolling to minimize global impact
    try {
      if (typeof window !== "undefined") {
        const yOffset = -80;
        const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
        
        // Scroll only the specific element into view
        window.scrollTo({
          top: y,
          behavior: "smooth"
        });
      }
    } catch (err) {
      console.error("Error scrolling to section:", err);
    }

    // Reset the flag after animation completes and store the timeout ref
    timeoutRef.current = setTimeout(() => {
      isScrollingRef.current = false;
      timeoutRef.current = null;
    }, 1000);
  };

  const sections = [
    {
      id: "introduction",
      title: "Introduction",
      icon: Info,
      color: "bg-linear-to-r from-blue-500 to-indigo-600",
      content: (
        <div>
          <p className="mb-4 leading-relaxed text-gray-700">
            At RentWise, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, 
            and safeguard your information when you use our website and services.
          </p>
          <p className="text-gray-700">
            By using our platform, you agree to the terms outlined in this policy. We may update this 
            policy periodically, and we encourage you to review it regularly to stay informed about how 
            we are protecting your information.
          </p>
        </div>
      )
    },
    {
      id: "what-we-collect",
      title: "What Information We Collect",
      icon: Info,
      color: "bg-linear-to-r from-blue-500 to-indigo-600",
      content: (
        <div className="backdrop-blur-sm bg-white/80 rounded-xl p-5 shadow-sm border border-blue-100">
          <p className="mb-4 leading-relaxed text-gray-700">
            We collect information you provide directly to us, such as when you create an account, use our services, or contact us for support.
          </p>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
            <div className="flex items-center gap-2 mb-2">
              <Info className="w-5 h-5 text-blue-600" />
              <h3 className="font-medium text-gray-900">Types of Information</h3>
            </div>
            <p className="text-sm text-gray-700">
              Personal information, property details, reviews, preferences, and usage data to improve your experience on RentWise.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "how-we-collect",
      title: "How We Collect Information",
      icon: Eye,
      color: "bg-linear-to-r from-purple-500 to-pink-600",
      content: (
        <div className="space-y-6">
          <div className="backdrop-blur-sm bg-white/80 rounded-xl p-5 shadow-sm border border-purple-100">
            <p className="text-gray-700 mb-4">
              We collect information through various methods to provide you with the best possible service experience.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              {
                title: "Direct Collection",
                description: "Information you provide when creating accounts, submitting reviews, or contacting us.",
                color: "from-indigo-50 to-blue-50",
                textColor: "text-indigo-700"
              },
              {
                title: "Automatic Collection", 
                description: "Technical information like IP addresses, browser type, and usage patterns.",
                color: "from-purple-50 to-pink-50",
                textColor: "text-purple-700"
              },
              {
                title: "Third-party Sources",
                description: "Information from partners, public databases, and social media platforms.",
                color: "from-emerald-50 to-teal-50",
                textColor: "text-emerald-700"
              },
              {
                title: "Cookies & Tracking",
                description: "Data collected through cookies, pixels, and similar tracking technologies.",
                color: "from-amber-50 to-orange-50",
                textColor: "text-amber-700"
              }
            ].map((item, index) => (
              <div key={index} className={`bg-linear-to-br ${item.color} p-5 rounded-xl shadow-sm`}>
                <div className="flex items-center gap-2 mb-3">
                  <div className={`p-2 rounded-full bg-white/70 ${item.textColor}`}>
                    <Eye className="w-5 h-5" />
                  </div>
                  <h3 className="font-medium text-gray-900">{item.title}</h3>
                </div>
                <p className="text-gray-700 text-sm">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      )
    },
    {
      id: "how-we-use",
      title: "How We Use Your Information",
      icon: Server,
      color: "bg-linear-to-r from-emerald-500 to-teal-600",
      content: (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <p className="text-gray-700 mb-4">
              We use the information we collect to provide, maintain, and improve our services, as well as to communicate with you about your account and our services.
            </p>
            
            <div className="bg-linear-to-br from-emerald-50 to-teal-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-2">Primary Uses</h3>
              <div className="space-y-2">
                {[
                  "Providing and personalizing our services",
                  "Processing transactions and communications", 
                  "Improving user experience and platform functionality",
                  "Ensuring security and preventing fraud",
                  "Complying with legal obligations"
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-1" />
                    <span className="text-sm text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "sharing",
      title: "Information Sharing",
      icon: Users,
      color: "bg-linear-to-r from-amber-500 to-orange-600",
      content: (
        <div className="space-y-6">
          <div className="backdrop-blur-sm bg-white/80 rounded-xl p-5 shadow-sm border border-amber-100">
            <p className="text-gray-700 mb-4">
              We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except as described in this policy.
            </p>
          </div>
          
          <div className="grid gap-4">
            {[
              {
                title: "Service Providers",
                description: "Trusted partners who help us operate our platform and provide services to you.",
                icon: Server,
              },
              {
                title: "Legal Requirements", 
                description: "When required by law, court order, or to protect our rights and safety.",
                icon: Shield,
              },
              {
                title: "Business Transfers",
                description: "In connection with mergers, acquisitions, or sale of company assets.",
                icon: Users,
              }
            ].map((item, index) => (
              <div key={index} className="bg-white/80 p-4 rounded-lg shadow-sm border border-amber-100">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-1.5 rounded-full bg-amber-100 text-amber-700">
                    <item.icon className="w-4 h-4" />
                  </div>
                  <h4 className="font-medium text-gray-900">{item.title}</h4>
                </div>
                <p className="text-sm text-gray-700">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      )
    },
    {
      id: "data-retention",
      title: "Data Retention",
      icon: MessageSquare,
      color: "bg-linear-to-r from-indigo-500 to-purple-600",
      content: (
        <div className="backdrop-blur-sm bg-white/80 rounded-xl p-6 shadow-sm border border-indigo-100">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 rounded-lg bg-indigo-100 text-indigo-700 mt-0.5">
              <MessageSquare className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">How Long We Keep Your Data</h3>
              <p className="text-gray-700">
                We retain your information for as long as necessary to provide our services and fulfill the purposes outlined in this policy.
              </p>
            </div>
          </div>
          
          <div className="bg-linear-to-br from-indigo-50 to-purple-50 p-4 rounded-lg">
            <h4 className="font-medium text-indigo-800 mb-2">Retention Guidelines</h4>
            <ul className="text-gray-700 text-sm space-y-2">
              <li>• Account information: Retained while your account is active</li>
              <li>• Reviews and content: Retained to maintain platform integrity</li>
              <li>• Usage data: Typically retained for 2-3 years for analytics</li>
              <li>• Legal records: Retained as required by applicable laws</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: "security",
      title: "Data Security",
      icon: Lock,
      color: "bg-linear-to-r from-red-500 to-pink-600",
      content: (
        <div className="backdrop-blur-sm bg-white/80 rounded-xl p-6 shadow-sm border border-red-100">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 rounded-lg bg-red-100 text-red-700 mt-0.5">
              <Lock className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Protecting Your Information</h3>
              <p className="text-gray-700">
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.
              </p>
            </div>
          </div>
          
          <div className="bg-linear-to-br from-red-50 to-pink-50 p-4 rounded-lg">
            <h4 className="font-medium text-red-800 mb-2">Security Measures</h4>
            <ul className="text-gray-700 text-sm space-y-2">
              <li>• SSL encryption for data transmission</li>
              <li>• Regular security audits and monitoring</li>
              <li>• Access controls and authentication</li>
              <li>• Data backup and recovery procedures</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: "your-rights",
      title: "Your Privacy Rights",
      icon: Shield,
      color: "bg-linear-to-r from-green-500 to-emerald-600",
      content: (
        <div className="backdrop-blur-sm bg-white/80 rounded-xl p-6 shadow-sm border border-green-100">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 rounded-lg bg-green-100 text-green-700 mt-0.5">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Control Your Data</h3>
              <p className="text-gray-700">
                You have certain rights regarding your personal information. We respect these rights and provide ways for you to exercise them.
              </p>
            </div>
          </div>
          
          <div className="bg-linear-to-br from-green-50 to-emerald-50 p-4 rounded-lg">
            <h4 className="font-medium text-green-800 mb-2">Your Rights Include</h4>
            <ul className="text-gray-700 text-sm space-y-2">
              <li>• Access: Request copies of your personal data</li>
              <li>• Correction: Update or correct inaccurate information</li>
              <li>• Deletion: Request removal of your personal data</li>
              <li>• Portability: Receive your data in a portable format</li>
              <li>• Objection: Object to certain processing of your data</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: "contact",
      title: "Contact Us",
      icon: Shield,
      color: "bg-linear-to-r from-violet-500 to-fuchsia-600",
      content: (
        <div>
          <p className="mb-5 text-gray-700">
            If you have questions about this Privacy Policy or our practices, please contact our Privacy Team at:
          </p>
          <div className="bg-linear-to-br from-violet-50 to-fuchsia-50 rounded-xl overflow-hidden shadow-sm">
            <div className="p-5 border-b border-violet-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-full bg-violet-100 text-violet-700">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-medium text-gray-900">Email</h3>
              </div>
              <p className="text-violet-700 font-medium">privacy@rentwise.com</p>
            </div>
            <div className="p-5 border-b border-fuchsia-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-full bg-fuchsia-100 text-fuchsia-700">
                  <Globe className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-medium text-gray-900">Address</h3>
              </div>
              <p className="text-gray-700">RentWise Privacy Office<br />123 Privacy Street<br />Security Town, ST 12345</p>
            </div>
            <div className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-full bg-purple-100 text-purple-700">
                  <Clock className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-medium text-gray-900">Response Time</h3>
              </div>
              <p className="text-gray-700">We strive to respond to all inquiries within 48 hours.</p>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div id="privacy-page" className="min-h-screen relative bg-linear-to-b from-gray-50 to-white overflow-y-auto overflow-x-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-20 left-0 w-64 h-64 rounded-full bg-indigo-100/30 blur-3xl -z-10" />
      <div className="absolute bottom-20 right-10 w-80 h-80 rounded-full bg-purple-100/20 blur-3xl -z-10" />
      <div className="absolute top-1/3 right-0 w-72 h-72 rounded-full bg-blue-100/30 blur-3xl -z-10" />
      
      {/* Header */}
      <header className="pt-12 pb-6 md:pt-20 md:pb-12 container mx-auto px-4 md:px-6">
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto"
        >
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 0.2, type: "spring" as const, stiffness: 100 }}
            className="mb-6 inline-flex"
          >
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-indigo-300 animate-ping opacity-20"></div>
              <div className="relative p-4 rounded-full bg-linear-to-r from-indigo-500 to-purple-600 shadow-lg">
                <Shield className="w-7 h-7 md:w-8 md:h-8 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="text-4xl md:text-5xl font-bold bg-linear-to-r from-indigo-600 to-violet-600 text-transparent bg-clip-text mb-4"
          >
            Privacy Policy
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.7 }}
            className="text-gray-600"
          >
            Last updated: April 23, 2025
          </motion.p>
        </motion.div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 md:px-6 pb-20">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar Navigation for Desktop */}
          <motion.aside 
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6, duration: 0.7 }}
            className="hidden md:block md:w-64 lg:w-72 shrink-0"
          >
            <div className="sticky top-8">
              <nav className="space-y-1 backdrop-blur-md bg-white/70 rounded-2xl p-3 shadow-sm border border-gray-100">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                      activeSection === section.id 
                        ? `${section.color} text-white shadow-sm` 
                        : "hover:bg-gray-50 text-gray-700"
                    }`}
                  >
                    <span className={`p-1.5 rounded-md ${activeSection === section.id ? "bg-white/20" : "bg-gray-100"}`}>
                      <section.icon className="w-5 h-5" />
                    </span>
                    <span className="font-medium">{section.title}</span>
                  </button>
                ))}
              </nav>

              <div className="mt-6 bg-linear-to-br from-indigo-50 to-purple-50 p-5 rounded-xl border border-indigo-100">
                <h3 className="font-medium text-gray-900 mb-2">Need Help?</h3>
                <p className="text-sm text-gray-700 mb-3">
                  If you have questions about your privacy or data, our team is here to help.
                </p>
                <button className="text-sm bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 transition-colors w-full">
                  Contact Support
                </button>
              </div>
            </div>
          </motion.aside>

          {/* Mobile Navigation - Pills */}
          <div className="md:hidden sticky top-0 z-10 pt-3 pb-4 bg-gray-50/80 backdrop-blur-sm">
            <div className="overflow-x-auto flex gap-2 p-1 snap-x">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-full whitespace-nowrap shrink-0 transition-colors snap-start ${
                    activeSection === section.id 
                      ? `${section.color} text-white` 
                      : "bg-white text-gray-800 shadow-sm"
                  }`}
                >
                  <section.icon className="w-5 h-5" />
                  <span className="text-sm font-medium">{section.title}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="grow max-w-3xl">
            {sections.map((section) => (
              <motion.section
                key={section.id}
                id={section.id}
                ref={(el) => { if (el) { sectionRefs.current[section.id] = el; } }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="mb-16 scroll-mt-20"
              >
                <div className={`relative ${section.color} text-white py-4 px-6 rounded-xl shadow-md mb-6`}>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                      <section.icon className="w-5 h-5" />
                    </div>
                    <h2 className="text-2xl font-bold">{section.title}</h2>
                  </div>
                </div>
                
                <div className="px-2">
                  {section.content}
                </div>

                {section.id !== sections[sections.length - 1].id && (
                  <div className="flex justify-end mt-8">
                    <button 
                      onClick={() => scrollToSection(sections[sections.findIndex(s => s.id === section.id) + 1].id)}
                      className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800"
                    >
                      <span>Next section</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </motion.section>
            ))}
        </div>
      </div>
    </main>
    </div>
  );
} 